import WolfAddStep from '@components/ui/WolfAddStep';
import WolfCardsStep from '@components/ui/WolfCardsStep';
import { EditIcon24 } from '@components/ui/WolfIcons';
import { graphQlClient } from '@config/graphqlClient';
import { closestCorners, DndContext, DragEndEvent, DragOverEvent, DragOverlay, DragStartEvent, Modifier, PointerSensor, useSensor, useSensors, CollisionDetection, pointerWithin, rectIntersection } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import PoliciesGraphQL from '@graphql/policy.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { getPoliciesRequest, Policy } from '@models/policy.model';
import { Process } from '@models/process.model';
import { Step, StepType } from '@models/step.model';
import { Trigger, TriggerType } from '@models/trigger.model';
import { UserRoles } from '@models/user.model';
import {
  BoltOutlined,
  CalendarMonthOutlined,
  CheckBoxOutlined,
  CloseOutlined,
  GroupWorkOutlined,
  PolicyOutlined
} from '@mui/icons-material';
import { Backdrop, Box, CircularProgress, Divider, Icon, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { RootState } from '@redux/store';
import { filterStepsInsideSwitchRepeat } from '@utils/filterStepsInsideSwitchRepeat';
import { getStepsInsideSwitchRepeat } from '@utils/getStepsInsideSwitchRepeat';
import { RecurrenceTranslations, repeatRuleToText } from '@utils/repeat-rule-to-text';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import WolfDropdown from './ui/WolfDropdown';
import WolfInput from './ui/WolfInput';
import WolfTextLink from './ui/WolfTextLink';
import WolfTooltip from './ui/WolfTooltip';

// Phantom drop zone component for placing items at the start or end
const PhantomDropZone: React.FC<{ id: string; isVisible: boolean; isOver: boolean; text?: string }> = ({ id, isVisible, isOver, text = 'Drop here' }) => {
  const { setNodeRef, attributes, listeners } = useSortable({ id });

  if (!isVisible) return null;

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        height: '60px',
        margin: '8px 0',
        border: '2px dashed',
        borderColor: isOver ? 'primary.main' : 'grey.400',
        backgroundColor: isOver ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        cursor: 'default',
      }}
    >
      <Typography variant="body12" color={isOver ? 'primary.main' : 'grey.600'}>
        {text}
      </Typography>
    </Box>
  );
};

// Drop zone for placing items as main step (after container's substeps)
const ContainerEndZone: React.FC<{ id: string; text: string; isVisible: boolean; isOver: boolean }> = ({ id, text, isVisible, isOver }) => {
  const { setNodeRef, attributes, listeners } = useSortable({ id });

  if (!isVisible) return null;

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        height: '50px',
        margin: '8px 0',
        padding: '16px',
        border: '2px dashed',
        borderColor: isOver ? 'primary.main' : 'grey.400',
        backgroundColor: isOver ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        cursor: 'default',
      }}
    >
      <Typography variant="body12" color={isOver ? 'primary.main' : 'grey.600'}>
        {text}
      </Typography>
    </Box>
  );
};

// Drop zone for placing items inside empty REPEAT/SWITCH containers
const EmptyContainerDropZone: React.FC<{ id: string; text: string; isVisible: boolean; isOver: boolean }> = ({ id, text, isVisible, isOver }) => {
  const { setNodeRef, attributes, listeners } = useSortable({ id });

  if (!isVisible) return null;

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        height: '60px',
        margin: '8px 0 8px 24px',
        border: '2px dashed',
        borderColor: isOver ? 'primary.main' : 'grey.400',
        backgroundColor: isOver ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        cursor: 'default',
      }}
    >
      <Typography variant="body12" color={isOver ? 'primary.main' : 'grey.600'}>
        {text}
      </Typography>
    </Box>
  );
};


// Custom modifier to snap drag overlay center to cursor position
const snapCenterToCursor: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (draggingNodeRect && activatorEvent && 'clientX' in activatorEvent) {
    const activatorCoordinates = {
      x: (activatorEvent as MouseEvent).clientX,
      y: (activatorEvent as MouseEvent).clientY,
    };

    return {
      ...transform,
      x: activatorCoordinates.x - draggingNodeRect.left - draggingNodeRect.width / 2 + transform.x,
      y: activatorCoordinates.y - draggingNodeRect.top - draggingNodeRect.height / 2 + transform.y,
    };
  }

  return transform;
};

// Custom collision detection: try pointerWithin first, fall back to closestCorners
const customCollisionDetection: CollisionDetection = (args) => {
  // First try pointer-based detection (most accurate for our use case)
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }

  // Fall back to rect intersection
  const rectCollisions = rectIntersection(args);
  if (rectCollisions.length > 0) {
    return rectCollisions;
  }

  // Final fallback to closestCorners
  return closestCorners(args);
};

interface ProcessSidebarProps {
  steps: any[];
  origProcess: Process | undefined;
  title: string;
  titleLink?: string;
  subTitle: string;
  subTitleLink?: string;
  category: string;
  goal: string;
  generatedFromName: string;
  policies: Policy[];
  triggers: Trigger[];
  triggeredBy: Step[];
  handleSelectStep: (stepId: string) => void;
  handleEditStep: (stepId: string) => void;
  handleEditTriggers: () => void;
  handleNewStep: (
    item: { text: string; value: string },
    index?: number,
    context?: { id: string; stepsIds: string; name: string },
  ) => void;
  dragEndHandler: (source: number, destination: number, loopId?: string) => void;
  dragInsideOutsideHandler: (moveInside: boolean, itemId: string, containerId: string, targetMainIndex?: number, targetSubstepIndex?: number) => void;
  selectedStep: string;
  editMode: boolean;
  handleProcessUpdate: (process: Process) => void;
  handleLinkProcessPolicies: (processId: string, policies: string[]) => void;
  saving: boolean;
  isExecuting?: boolean;
  disableAddStep?: boolean;
  stepWithUnsavedChanges?: string;
  editingStepTitle?: string;
}

const ProcessSidebar: React.FC<ProcessSidebarProps> = ({
  origProcess,
  selectedStep,
  title,
  titleLink,
  subTitle,
  subTitleLink,
  goal,
  generatedFromName,
  policies,
  category,
  triggers = [],
  triggeredBy = [],
  steps,
  editMode,
  dragEndHandler,
  dragInsideOutsideHandler,
  handleEditTriggers,
  handleSelectStep,
  handleEditStep,
  handleNewStep,
  handleProcessUpdate,
  handleLinkProcessPolicies,
  saving,
  isExecuting = false,
  disableAddStep = false,
  stepWithUnsavedChanges,
  editingStepTitle,
}) => {
  const localeProcesses = useTranslation('processes');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');
  const localeCalTrig = useTranslation('calTrig');
  const dispatch = useAppDispatch();

  const recurrenceTranslations = useMemo((): RecurrenceTranslations => ({
    dayNames: (localeCalTrig as any)['dayNames'] || {},
    monthNames: (localeCalTrig as any)['monthNames'] || {},
    positionNames: (localeCalTrig as any)['positionNames'] || {},
    recurrencePatterns: (localeCalTrig as any)['recurrencePatterns'] || {},
  }), [localeCalTrig]);
  const history = useHistory();
  const { role } = useAppSelector((state: RootState) => state.user);

  const [isHoveringGoal, setIsHoveringGoal] = useState(false);
  const [isHoveringTriggers, setIsHoveringTriggers] = useState(false);
  const [isHoveringGeneratedFrom, setIsHoveringGeneratedFrom] = useState(false);
  const [isHoveringPolicies, setIsHoveringPolicies] = useState(false);
  const [isHoveringCategory, setIsHoveringCategory] = useState(false);
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [isEditingPolicies, setIsEditingPolicies] = useState(false);
  const [tempGoal, setTempGoal] = useState(goal);
  const [tempCategory, setTempCategory] = useState(category);
  const [policyList, setPolicyList] = useState<{ text: string; value: string }[]>([]);
  const [selectedPolicies, setSelectedPolicies] = useState<{ text: string; value: string }[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [recentlyDroppedId, setRecentlyDroppedId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState<boolean>(false);

  const handleMouseEnterGoal = useCallback(() => setIsHoveringGoal(true), []);
  const handleMouseLeaveGoal = useCallback(() => setIsHoveringGoal(false), []);
  const handleMouseEnterTriggers = useCallback(() => setIsHoveringTriggers(true), []);
  const handleMouseLeaveTriggers = useCallback(() => setIsHoveringTriggers(false), []);
  const handleMouseEnterGeneratedFrom = useCallback(() => setIsHoveringGeneratedFrom(true), []);
  const handleMouseLeaveGeneratedFrom = useCallback(() => setIsHoveringGeneratedFrom(false), []);
  const handleMouseEnterPolicies = useCallback(() => setIsHoveringPolicies(true), []);
  const handleMouseLeavePolicies = useCallback(() => setIsHoveringPolicies(false), []);
  const handleMouseEnterCategory = useCallback(() => setIsHoveringCategory(true), []);
  const handleMouseLeaveCategory = useCallback(() => setIsHoveringCategory(false), []);

  useEffect(() => {
    setTempGoal(goal);
  }, [goal]);

  useEffect(() => {
    setTempCategory(category);
  }, [category]);

  useEffect(() => {
    if (policies) {
      setSelectedPolicies(policies.map((policy) => ({ text: policy.name, value: policy.policyId })));
    }
  }, [policies]);

  // Clear isReordering when saving completes
  useEffect(() => {
    if (!saving && isReordering) {
      setIsReordering(false);
    }
  }, [saving, isReordering]);

  const handleShare = (stepId: string) => {
    navigator.clipboard.writeText(window.location.href.split('?')[0] + '?stepId=' + stepId);
    dispatch(appendActionMessage({ message: localeActionMessages['linkCopied'], type: SnackType.DARK }));
  };

  const getPolicies = async () => {
    try {
      const data: getPoliciesRequest = await graphQlClient.request(PoliciesGraphQL.queries.getPolicies);
      setPolicyList(
        data?.getPolicies.map((item) => {
          return { text: item.name, value: item.policyId };
        }) || [],
      );
    } catch (e: any) {
      // Silently handle policy fetch errors
    }
  };

  const mainSteps = filterStepsInsideSwitchRepeat(steps)
    .sort((a, b) => a.order - b.order)
    .map((step: Step, index: number) => ({
      stepId: step?.stepId,
      name: step?.name,
      type: step?.stepType,
      stepsIds: step?.stepData?.stepsIds,
      position: (index + 1).toString(),
      order: step?.order,
    }));

  // Build a flat array of all steps (main + substeps) in display order
  const allStepsFlat: Array<{
    stepId: string;
    name: string;
    type: StepType;
    position: string;
    isSubstep: boolean;
    parentStepId?: string;
    parentType?: StepType;
    isLast?: boolean;
    drawLine?: boolean;
    order: number;
  }> = [];

  mainSteps.forEach((mainStep, mainIndex) => {
    // Add main step
    allStepsFlat.push({
      stepId: mainStep.stepId,
      name: mainStep.name,
      type: mainStep.type,
      position: mainStep.position,
      isSubstep: false,
      order: mainStep.order || -1,
    });

    // Add substeps if this is a SWITCH/REPEAT step
    if (mainStep.type === StepType.SWITCH || mainStep.type === StepType.REPEAT) {
      const substeps = getStepsInsideSwitchRepeat(mainStep.stepsIds || '', steps)
        .sort((a, b) => a.order - b.order);

      substeps.forEach((substep, subIndex) => {
        allStepsFlat.push({
          stepId: substep.stepId,
          name: substep.name,
          type: mainStep.type,
          position:
            mainStep.type === StepType.SWITCH
              ? mainIndex + 1 + '.' + String.fromCharCode(subIndex + 65)
              : mainIndex + 1 + '.' + (subIndex + 1),
          isSubstep: true,
          parentStepId: mainStep.stepId,
          parentType: mainStep.type,
          isLast: subIndex === substeps.length - 1,
          drawLine: mainStep.type === StepType.SWITCH,
          order: substep.order || -1,
        });
      });
    }
  });

  // Check if the dragged step is a SWITCH or REPEAT (container) - these cannot be dropped inside other containers
  const isDraggingContainer = activeId ? (() => {
    const draggedStep = allStepsFlat.find(s => s.stepId === activeId);
    return draggedStep?.type === StepType.SWITCH || draggedStep?.type === StepType.REPEAT;
  })() : false;

  const getSubMaxOrder = (stepsIds: string): number => {
    return getStepsInsideSwitchRepeat(stepsIds, steps).sort((a, b) => (a.order > b.order ? -1 : 1))[0]?.order || 0;
  };

  // Constants for drop zone IDs - must be defined before stepsList
  const PHANTOM_START_ID = '__PHANTOM_START__';
  const PHANTOM_END_ID = '__PHANTOM_END__';
  const CONTAINER_END_PREFIX = '__CONTAINER_END__';
  const EMPTY_CONTAINER_PREFIX = '__EMPTY_CONTAINER__';

  // Feature flags
  const SHOW_CONTAINER_BORDERS = false; // Set to true to show colored borders around SWITCH/REPEAT containers during drag

  // Wrapper functions to handle loading state after drag operations
  const dragEndHandlerWithLoading = useCallback((sourceIndex: number, destIndex: number, loopId?: string) => {
    dragEndHandler(sourceIndex, destIndex, loopId);
  }, [dragEndHandler]);

  const dragInsideOutsideHandlerWithLoading = useCallback((moveInside: boolean, itemId: string, containerId: string, targetMainIndex?: number, targetSubstepIndex?: number) => {
    dragInsideOutsideHandler(moveInside, itemId, containerId, targetMainIndex, targetSubstepIndex);
  }, [dragInsideOutsideHandler]);

  const handleFlatDragEnd = useCallback((sourceIndex: number, destIndex: number) => {

    if (sourceIndex === destIndex) {
      setIsReordering(false);
      return;
    }

    const draggedItem = allStepsFlat[sourceIndex];
    const targetItem = allStepsFlat[destIndex];

    if (!draggedItem || !targetItem) {
      return;
    }

    // Prevent SWITCH/REPEAT steps from being dragged into substeps
    if (!draggedItem.isSubstep &&
        (draggedItem.type === StepType.SWITCH || draggedItem.type === StepType.REPEAT) &&
        targetItem.isSubstep) {
      dispatch(appendActionMessage({
        message: localeProcesses['cannotMoveContainerInside'],
        type: SnackType.ERROR
      }));
      return;
    }

    // Determine if we're moving between levels (main <-> substep)
    const wasSubstep = draggedItem.isSubstep;
    const isNowSubstep = targetItem.isSubstep;


    // Case 1: Both are substeps of the same parent - just reorder
    if (wasSubstep && isNowSubstep && draggedItem.parentStepId === targetItem.parentStepId) {
      // Validate parentStepId exists
      if (!draggedItem.parentStepId) {
        console.error('Invalid drag operation: parentStepId is undefined');
        setIsReordering(false);
        return;
      }

      // Calculate actual indices within the parent's substeps array
      const parentSteps = allStepsFlat.filter(s => s.isSubstep && s.parentStepId === draggedItem.parentStepId);
      const actualSourceIndex = parentSteps.findIndex(s => s.stepId === draggedItem.stepId);
      const actualDestIndex = parentSteps.findIndex(s => s.stepId === targetItem.stepId);

      dragEndHandlerWithLoading(actualSourceIndex, actualDestIndex, draggedItem.parentStepId);
    }
    // Case 2: Both are main steps
    else if (!wasSubstep && !isNowSubstep) {
      // When dragging over a main step (including containers), place BEFORE it at main level
      // To place items inside a container, drag over its substeps instead
      const mainStepsOnly = allStepsFlat.filter(s => !s.isSubstep);
      const actualSourceIndex = mainStepsOnly.findIndex(s => s.stepId === draggedItem.stepId);
      const actualDestIndex = mainStepsOnly.findIndex(s => s.stepId === targetItem.stepId);
      dragEndHandlerWithLoading(actualSourceIndex, actualDestIndex);
    }
    // Case 3: Moving substep to main level
    else if (wasSubstep && !isNowSubstep) {
      // Validate parentStepId exists
      if (!draggedItem.parentStepId) {
        console.error('Invalid drag operation: parentStepId is undefined');
        setIsReordering(false);
        return;
      }

      // Moving to main level - place BEFORE the target main step
      const mainStepsOnly = allStepsFlat.filter(s => !s.isSubstep);
      const targetIndexInMain = mainStepsOnly.findIndex(s => s.stepId === targetItem.stepId);
      dragInsideOutsideHandlerWithLoading(false, draggedItem.stepId, draggedItem.parentStepId, targetIndexInMain);
    }
    // Case 4: Moving main step to substep level
    else if (!wasSubstep && isNowSubstep) {
      // Validate parentStepId exists
      if (!targetItem.parentStepId) {
        console.error('Invalid drag operation: target parentStepId is undefined');
        setIsReordering(false);
        return;
      }

      // Calculate target index within the container's substeps
      const siblingSubsteps = allStepsFlat.filter(s => s.isSubstep && s.parentStepId === targetItem.parentStepId);
      const hoveredIndex = siblingSubsteps.findIndex(s => s.stepId === targetItem.stepId);
      const isFirstSubstep = hoveredIndex === 0;

      // If hovering over first substep, insert at position 0 (before it)
      // Otherwise, insert after the hovered substep
      const targetSubstepIndex = isFirstSubstep ? 0 : hoveredIndex + 1;
      dragInsideOutsideHandlerWithLoading(true, draggedItem.stepId, targetItem.parentStepId, undefined, targetSubstepIndex);
    }
    // Case 5: Moving between different substep parents
    else if (wasSubstep && isNowSubstep && draggedItem.parentStepId !== targetItem.parentStepId) {
      // Validate both parentStepIds exist
      if (!draggedItem.parentStepId || !targetItem.parentStepId) {
        console.error('Invalid drag operation: parentStepId is undefined');
        setIsReordering(false);
        return;
      }

      // Calculate target index within the new container's substeps
      const siblingSubsteps = allStepsFlat.filter(s => s.isSubstep && s.parentStepId === targetItem.parentStepId);
      const hoveredIndex = siblingSubsteps.findIndex(s => s.stepId === targetItem.stepId);
      const isFirstSubstep = hoveredIndex === 0;

      // If hovering over first substep, insert at position 0 (before it)
      // Otherwise, insert after the hovered substep
      const targetSubstepIndex = isFirstSubstep ? 0 : hoveredIndex + 1;
      dragInsideOutsideHandler(false, draggedItem.stepId, draggedItem.parentStepId);
      setTimeout(() => {
        if (targetItem.parentStepId) {
          dragInsideOutsideHandlerWithLoading(true, draggedItem.stepId, targetItem.parentStepId, undefined, targetSubstepIndex);
        }
      }, 400);
    } else {
      setIsReordering(false);
    }
  }, [allStepsFlat, dragEndHandlerWithLoading, dragInsideOutsideHandlerWithLoading, dragEndHandler, dragInsideOutsideHandler, dispatch]);

  const calendarTriggerList = triggers
    ?.filter((item) => item.triggerType === TriggerType.CALENDAR)
    .map((item) => (
      <Box className="flex items-center w-full my-1" key={item.triggerId}>
        <CalendarMonthOutlined />
        <WolfTooltip
          title={
            (item?.triggerData?.description || '') +
            (item?.triggerData?.recurrence
              ? ' - ' + repeatRuleToText(item?.triggerData?.recurrence, recurrenceTranslations)
              : localeProcesses['noRecurrence'])
          }
          placement="right">
          <Typography variant="body14" className="!ml-1">
            {item.name}
          </Typography>
        </WolfTooltip>
      </Box>
    ));

  const eventTriggerList = triggers
    ?.filter((item) => item.triggerType === TriggerType.EVENT)
    .map((item) => (
      <Box className="flex items-center w-full my-1" key={item.triggerId}>
        <BoltOutlined />
        <WolfTooltip title={item?.triggerData?.description} placement="right">
          <Typography variant="body14" className="!ml-1">
            {item.name}
          </Typography>
        </WolfTooltip>
      </Box>
    ));

  const triggeredFrom = triggeredBy?.map((item) => (
    <Box
      className="flex items-center w-full my-1 cursor-pointer hover:opacity-70"
      key={item?.stepId + 'triggeredBy'}
      onClick={(e) => {
        const url = `/processes/${item?.process?.processId}`;
        if (e.ctrlKey || e.metaKey) {
          window.open(url, '_blank');
        } else {
          history.push(url);
        }
      }}>
      <GroupWorkOutlined />
      <WolfTooltip title={item?.process?.goal} placement="right">
        <Typography variant="body14" className="!ml-1">
          {item?.process?.name}
        </Typography>
      </WolfTooltip>
    </Box>
  ));

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleUnifiedDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;


    const droppedStepId = active.id as string;

    setActiveId(null);
    setOverId(null);

    if (!over) {
      return;
    }

    // Check if the dragged step is in-progress (not saved yet)
    if (droppedStepId.startsWith('in-progress-') || !droppedStepId) {
      dispatch(appendActionMessage({
        message: localeProcesses['saveStepBeforeMoving'] || 'You must save the step before trying to move it',
        type: SnackType.WARNING
      }));
      return;
    }

    // Set loading state to prevent further drag operations
    setIsReordering(true);

    // Highlight the dropped step
    setRecentlyDroppedId(droppedStepId);
    setTimeout(() => {
      setRecentlyDroppedId(null);
    }, 5000);

    

    // Check if dropped on empty container zone (inside empty REPEAT/SWITCH)
    if ((over.id as string).startsWith(EMPTY_CONTAINER_PREFIX)) {
      const containerId = (over.id as string).replace(EMPTY_CONTAINER_PREFIX, '');
      const draggedItem = allStepsFlat.find(s => s.stepId === active.id);

      if (draggedItem) {
        // Prevent SWITCH/REPEAT containers from being nested
        if (!draggedItem.isSubstep && (draggedItem.type === StepType.SWITCH || draggedItem.type === StepType.REPEAT)) {
          dispatch(appendActionMessage({
            message: localeProcesses['cannotMoveContainerInside'],
            type: SnackType.ERROR
          }));
          setIsReordering(false);
          return;
        }

        if (draggedItem.isSubstep && draggedItem.parentStepId) {
          // Moving from another container - first remove from old parent
          dragInsideOutsideHandler(false, draggedItem.stepId, draggedItem.parentStepId);
          // Then  to new container as first substep (index 0)
          setTimeout(() => {
            dragInsideOutsideHandlerWithLoading(true, draggedItem.stepId, containerId, undefined, 0);
          }, 400);
        } else {
          // Moving from main level - add to container as first substep (index 0)
          dragInsideOutsideHandlerWithLoading(true, draggedItem.stepId, containerId, undefined, 0);
        }
      }
      return;
    }

    // Check if dropped on container end zone (next main step after container)
    if ((over.id as string).startsWith(CONTAINER_END_PREFIX)) {
      const containerId = (over.id as string).replace(CONTAINER_END_PREFIX, '');
      const draggedItem = allStepsFlat.find(s => s.stepId === active.id);

      if (draggedItem) {
        // Find the container's position in main steps
        const mainStepsOnly = allStepsFlat.filter(s => !s.isSubstep);
        const containerIndex = mainStepsOnly.findIndex(s => s.stepId === containerId);
        const targetIndex = containerIndex + 1; // Position right after the container


        if (draggedItem.isSubstep) {
          // Validate parentStepId exists
          if (!draggedItem.parentStepId) {
            console.error('Invalid drag operation: parentStepId is undefined');
            setIsReordering(false);
            return;
          }
          // Move substep out to main level at target position
          dragInsideOutsideHandlerWithLoading(false, draggedItem.stepId, draggedItem.parentStepId, targetIndex);
        } else {
          // Reorder main step to target position
          const sourceIndex = mainStepsOnly.findIndex(s => s.stepId === draggedItem.stepId);
          if (sourceIndex !== targetIndex && sourceIndex !== targetIndex - 1) {
            // Adjust for removal: if source is before target, target shifts down by 1
            const adjustedTarget = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
            dragEndHandlerWithLoading(sourceIndex, adjustedTarget);
          } else {
            setIsReordering(false);
          }
        }
      }
      return;
    }

    // Check if dropped on phantom start zone
    if (over.id === PHANTOM_START_ID) {
      const draggedItem = allStepsFlat.find(s => s.stepId === active.id);

      if (draggedItem) {
        // If it's a substep, move it to main level (out of parent)
        if (draggedItem.isSubstep) {
          // Validate parentStepId exists
          if (!draggedItem.parentStepId) {
            console.error('Invalid drag operation: parentStepId is undefined');
            setIsReordering(false);
            return;
          }

          // Move to main level at the start (index 0)
          dragInsideOutsideHandlerWithLoading(false, draggedItem.stepId, draggedItem.parentStepId, 0);
        } else {
          // It's already a main step, reorder to start
          const mainStepsOnly = allStepsFlat.filter(s => !s.isSubstep);
          const actualSourceIndex = mainStepsOnly.findIndex(s => s.stepId === draggedItem.stepId);
          const destIndex = 0; // Move to first position

          if (actualSourceIndex >= 0 && actualSourceIndex !== destIndex) {
            dragEndHandlerWithLoading(actualSourceIndex, destIndex);
          } else {
            setIsReordering(false);
          }
        }
      }
      return;
    }

    // Check if dropped on phantom end zone
    if (over.id === PHANTOM_END_ID) {
      const draggedItem = allStepsFlat.find(s => s.stepId === active.id);

      if (draggedItem) {

        // If it's a substep, move it to main level (out of parent)
        if (draggedItem.isSubstep) {
          // Validate parentStepId exists
          if (!draggedItem.parentStepId) {
            console.error('Invalid drag operation: parentStepId is undefined');
            setIsReordering(false);
            return;
          }

          // First, remove from parent
          dragInsideOutsideHandler(false, draggedItem.stepId, draggedItem.parentStepId);

          // Then, after a delay to let the first operation complete, reorder to end
          setTimeout(() => {
            const currentMainSteps = filterStepsInsideSwitchRepeat(steps).sort((a, b) => a.order - b.order);
            const sourceIndex = currentMainSteps.findIndex(s => s.stepId === draggedItem.stepId);
            const destIndex = currentMainSteps.length - 1;

            if (sourceIndex >= 0 && sourceIndex !== destIndex) {
              dragEndHandlerWithLoading(sourceIndex, destIndex);
            } else {
              setIsReordering(false);
            }
          }, 200);
        } else {
          // It's already a main step, reorder to end
          const mainStepsOnly = allStepsFlat.filter(s => !s.isSubstep);
          const actualSourceIndex = mainStepsOnly.findIndex(s => s.stepId === draggedItem.stepId);
          // To move to the END, we need to insert AFTER the last item
          // After removing source item, the array has (length - 1) items
          // So destination should be (length - 1) to insert at the end
          const destIndex = mainStepsOnly.length - 1;


          if (actualSourceIndex >= 0 && actualSourceIndex !== destIndex) {
            dragEndHandlerWithLoading(actualSourceIndex, destIndex);
          } else {
            setIsReordering(false);
          }
        }
      }
      return;
    }

    // Find source and destination indices in the flat array
    let sourceIndex = allStepsFlat.findIndex(s => s.stepId === active.id);
    let destIndex = allStepsFlat.findIndex(s => s.stepId === over.id);

    const hoveredItem = allStepsFlat[destIndex];

    // Special handling: If dropping on a SWITCH/REPEAT container that has substeps,
    // redirect to the first substep of that container (place inside)
    // UNLESS the dragged item is a main step positioned before the container (trying to place above/before)
    if (hoveredItem && !hoveredItem.isSubstep &&
        (hoveredItem.type === StepType.SWITCH || hoveredItem.type === StepType.REPEAT)) {
      const firstSubstep = allStepsFlat.find(s => s.isSubstep && s.parentStepId === hoveredItem.stepId);
      const draggedItem = allStepsFlat[sourceIndex];

      // Only redirect to inside if:
      // - It's a substep (always go inside)
      // - OR it's a main step being dragged DOWN (sourceIndex < destIndex = moving down in list = place inside)
      const shouldPlaceInside = draggedItem?.isSubstep || sourceIndex < destIndex;

      if (firstSubstep && shouldPlaceInside) {
        // Redirect to first substep (place inside as first substep)
        destIndex = allStepsFlat.findIndex(s => s.stepId === firstSubstep.stepId);
      }
    }

    // For substeps: shift to target the NEXT item (drop after instead of before)
    // For main steps: keep default behavior (drop before)
    const finalHoveredItem = allStepsFlat[destIndex];

    // Only apply +1 shift for substeps (staying inside container)
    // BUT: don't shift for the FIRST substep - allow dropping at position 1
    // Container steps and regular main steps use default "drop before" behavior
    if (finalHoveredItem && finalHoveredItem.isSubstep) {
      // Check if this is the first substep of its parent
      const siblingSubsteps = allStepsFlat.filter(s => s.isSubstep && s.parentStepId === finalHoveredItem.parentStepId);
      const isFirstSubstep = siblingSubsteps.length > 0 && siblingSubsteps[0].stepId === finalHoveredItem.stepId;

      if (isFirstSubstep) {
        // First substep - don't shift, allow dropping at first position
      } else if (destIndex < allStepsFlat.length - 1) {
        const nextItem = allStepsFlat[destIndex + 1];

        // If next item is also a substep (staying in container), shift to drop after
        // ONLY when dragging from above (sourceIndex < destIndex)
        // When dragging from below, the position is already correct
        if (nextItem.isSubstep && sourceIndex < destIndex) {
          destIndex = destIndex + 1;
        } else {
          // Next item is a main step - don't shift, keep default behavior for exiting container
        }
      }
    }
    // For main steps (including containers), keep default "drop before" behavior


    if (sourceIndex >= 0 && destIndex >= 0) {
      handleFlatDragEnd(sourceIndex, destIndex);
    } else {
    }
  };

  const handleUnifiedDragStart = (event: DragStartEvent) => {
    const draggedId = event.active.id as string;
    setActiveId(draggedId);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverId(over ? over.id as string : null);
  };

  const stepsList = allStepsFlat.map((item, index) => {
    const isSwitchRepeat = !item.isSubstep && (item.type === StepType.SWITCH || item.type === StepType.REPEAT);
    const mainStepData = mainSteps.find(s => s.stepId === item.stepId);

    // Check if we should show a drop placeholder for this item (hovering over it)
    const isHoveringOverItem = activeId && overId === item.stepId && activeId !== item.stepId;
    let showPlaceholder = false;
    let placeholderPosition: 'before' | 'after' = 'before';

    if (isHoveringOverItem) {
      // For SWITCH/REPEAT containers: check if we're actually hovering over a substep
      if (isSwitchRepeat) {
        const substeps = allStepsFlat.filter(s => s.isSubstep && s.parentStepId === item.stepId);
        const hoveringOverSubstep = substeps.some(s => s.stepId === overId);

        // Don't show container placeholder if hovering over its substeps
        if (!hoveringOverSubstep) {
          showPlaceholder = true;
          placeholderPosition = 'before';
        }
      } else if (item.isSubstep) {
        // For substeps: show indicator after this substep
        showPlaceholder = true;
        placeholderPosition = 'after';
      } else {
        // Regular main step
        showPlaceholder = true;
        placeholderPosition = 'before';
      }
    }

    // Determine container border color (yellow for SWITCH, green for REPEAT)
    const isPartOfContainer = isSwitchRepeat || item.isSubstep;
    const isSwitch = isSwitchRepeat ? item.type === StepType.SWITCH : item.parentType === StepType.SWITCH;
    const showContainerBorder = activeId && isPartOfContainer;

    // Determine border sides based on position in container group
    // Uses ::before/::after pseudo-elements for continuous borders
    const getBorderStyles = (): Record<string, any> => {
      if (!showContainerBorder || !SHOW_CONTAINER_BORDERS) return {};

      const borderColor = isSwitch ? 'utilityYellow.main' : 'utilityGreen.main';

      if (isSwitchRepeat) {
        // Container step: use ::before for top border and ::after for side borders extending down
        const hasSubsteps = allStepsFlat.some(s => s.isSubstep && s.parentStepId === item.stepId);
        return {
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            backgroundColor: borderColor,
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: hasSubsteps ? '-16px' : 0,
            borderLeft: '4px solid',
            borderRight: '4px solid',
            borderColor,
            pointerEvents: 'none',
          },
        };
      } else if (item.isSubstep) {
        // Substep: use ::after for side borders, extending beyond element bounds
        return {
          position: 'relative',
          marginLeft: '0',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-8px',
            left: 0,
            right: 0,
            bottom: item.isLast ? 0 : '-8px',
            borderLeft: '4px solid',
            borderRight: '4px solid',
            borderBottom: item.isLast ? '4px solid' : 'none',
            borderColor,
            borderBottomLeftRadius: item.isLast ? '8px' : '0',
            borderBottomRightRadius: item.isLast ? '8px' : '0',
            pointerEvents: 'none',
          },
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingBottom: item.isLast ? '4px' : '0',
        };
      }
      return {};
    };

    // Determine if we should render the ContainerEndZone after this item
    const renderContainerEndZone = item.isSubstep && item.isLast && item.parentStepId && (() => {
      const parentStep = mainSteps.find(s => s.stepId === item.parentStepId);
      const parentIndex = mainSteps.findIndex(s => s.stepId === item.parentStepId);
      const isLastContainer = parentIndex === mainSteps.length - 1;

      // Don't show if this is the last main step (phantom end zone handles that)
      if (isLastContainer) return null;

      return (
        <ContainerEndZone
          id={`${CONTAINER_END_PREFIX}${item.parentStepId}`}
          text={localeProcesses['dropBelowStep'].replace('{stepName}', parentStep?.name || '')}
          isVisible={!!activeId}
          isOver={overId === `${CONTAINER_END_PREFIX}${item.parentStepId}`}
        />
      );
    })();

    // Check if this is an empty SWITCH/REPEAT container
    // Don't show drop zone if dragging a container (SWITCH/REPEAT can't be dropped inside other containers)
    const hasSubsteps = isSwitchRepeat && allStepsFlat.some(s => s.isSubstep && s.parentStepId === item.stepId);
    const shouldRenderEmptyContainerZone = isSwitchRepeat && !hasSubsteps && !isDraggingContainer;

    return (
      <React.Fragment key={item.stepId}>
        <Box
          sx={{
            position: 'relative',
            marginLeft: item.isSubstep ? '24px' : '0',
            ...getBorderStyles(),
          }}
        >
          {/* Drop indicator - shows where the step will be placed */}
          {false && showPlaceholder && (
            <Box
              sx={{
                position: 'absolute',
                [placeholderPosition === 'before' ? 'top' : 'bottom']: placeholderPosition === 'before' ? '-12px' : '-12px',
                left: item.isSubstep ? '24px' : '0',
                right: '0',
                height: '64px',
                backgroundColor: 'rgba(37, 99, 235, 0.1)',
                border: '2px dashed',
                borderColor: 'primary.main',
                borderRadius: '8px',
                zIndex: 1000,
                transition: 'all 0.2s ease',
              }}
            />
          )}

          <Box>
            <WolfCardsStep
              selected={item.stepId === selectedStep}
              item={item}
              index={index}
              onClick={() => handleSelectStep(item.stepId)}
              onEdit={() => handleEditStep(item.stepId)}
              dragDisabled={isReordering || isExecuting || editMode}
              isEditing={editMode}
              isExecuting={isExecuting}
              isUnsaved={item.stepId?.startsWith('in-progress-')}
              hasUnsavedChanges={item.stepId === stepWithUnsavedChanges}
              editingTitle={item.stepId === selectedStep ? editingStepTitle : undefined}
              onShare={() => handleShare(item.stepId)}
              isDraggingGlobal={false}
              isRecentlyDropped={recentlyDroppedId === item.stepId}
              substep={item.isSubstep}
              drawLine={item.drawLine}
              isLast={item.isLast}
              canAddSubstep={isSwitchRepeat && !item.stepId?.startsWith('in-progress-') && !disableAddStep}
              onSubAdd={
                isSwitchRepeat
                  ? () =>
                      handleNewStep({ text: '', value: '' }, getSubMaxOrder(mainStepData?.stepsIds || ''), {
                        id: item.stepId,
                        stepsIds: mainStepData?.stepsIds || '',
                        name: item.name,
                      })
                  : undefined
              }
            />
          </Box>
        </Box>

        {/* Empty container drop zone - shown when SWITCH/REPEAT has no substeps */}
        {shouldRenderEmptyContainerZone && (
          <EmptyContainerDropZone
            id={`${EMPTY_CONTAINER_PREFIX}${item.stepId}`}
            text={localeProcesses['dropInsideStep'].replace('{stepName}', item.name)}
            isVisible={!!activeId}
            isOver={overId === `${EMPTY_CONTAINER_PREFIX}${item.stepId}`}
          />
        )}

        {/* Container end zone - outside the bordered box, after last substep */}
        {renderContainerEndZone}
      </React.Fragment>
    );
  });

  // Find the active item for DragOverlay
  const activeStep = activeId ? [...steps, ...steps.flatMap(s => getStepsInsideSwitchRepeat(s.stepData?.stepsIds || '', steps))].find(s => s.stepId === activeId) : null;

  // Build all step IDs including drop zones
  const allStepIdsWithZones: string[] = [];
  allStepIdsWithZones.push(PHANTOM_START_ID); // Add start zone at the beginning
  allStepsFlat.forEach((step) => {
    allStepIdsWithZones.push(step.stepId);

    // Add empty container zone for SWITCH/REPEAT steps with no substeps
    if (!step.isSubstep && (step.type === StepType.SWITCH || step.type === StepType.REPEAT)) {
      const hasSubsteps = allStepsFlat.some(s => s.isSubstep && s.parentStepId === step.stepId);
      if (!hasSubsteps) {
        allStepIdsWithZones.push(`${EMPTY_CONTAINER_PREFIX}${step.stepId}`);
      }
    }

    // Add container end zone after the last substep of each container
    if (step.isSubstep && step.isLast) {
      allStepIdsWithZones.push(`${CONTAINER_END_PREFIX}${step.parentStepId}`);
    }
  });
  allStepIdsWithZones.push(PHANTOM_END_ID);

  const allStepIds = allStepIdsWithZones;

  const handleGoalUpdate = (e: any) => {
    setTempGoal(e.target.value);
  };

  const handleCategoryUpdate = (e: any) => {
    setTempCategory(e.target.value);
  };

  const handleEditPolicies = () => {
    setIsEditingPolicies(true);
    getPolicies();
  };

  const handleGoalSubmit = () => {
    if (tempGoal && tempGoal !== goal && origProcess) {
      handleProcessUpdate({
        ...origProcess,
        goal: tempGoal,
      });
    }
    setIsEditingGoal(false);
  };

  const handlePolicyChange = (policies: { text: string; value: string }[]) => {
    if (policies.length === 0) {
      setSelectedPolicies([]);
    } else {
      setSelectedPolicies([...policies] || []);
    }
  };

  const handlePolicySubmit = () => {
    setIsEditingPolicies(false);
    if (origProcess?.processId) {
      handleLinkProcessPolicies(
        origProcess?.processId,
        selectedPolicies.map((item) => item.value),
      );
    }
  };

  const handleCategorySubmit = () => {
    if (tempCategory !== category && origProcess) {
      handleProcessUpdate({
        ...origProcess,
        category: tempCategory,
      });
    }
    setIsEditingCategory(false);
  };

  const handleClearGeneratedFrom = () => {
    if (origProcess) {
      handleProcessUpdate({
        ...origProcess,
        generatedFromName: '',
        generatedFrom: undefined,
      });
    }
  };

  return (
    <Box className="px-4 overflow-y-auto h-full">
      <Box className="flex flex-wrap">
        <>
          <Box className="flex items-center relative !mb-3">
            <Box className="flex items-center w-11/12">
              <Box className="mr-2 my-1 flex">
                <GroupWorkOutlined />
              </Box>
              <Box className="flex items-center">
                <Typography variant="h3" className="flex items-center whitespace-nowrap text-ellipsis">
                  <WolfTextLink
                    variant="body14"
                    component="span"
                    className="inline"
                    onClick={(e?: React.MouseEvent) => {
                      if (e && (e.ctrlKey || e.metaKey)) {
                        window.open('/areas', '_blank');
                      } else {
                        history.push('/areas');
                      }
                    }}>
                    {localeCommon['areas']}
                  </WolfTextLink>
                  {title && (
                    <>
                      <span className="mx-0.5 inline text-sm">/</span>
                      <WolfTextLink
                        variant="body14"
                        component="span"
                        className="inline"
                        onClick={(e?: React.MouseEvent) => {
                          const url = titleLink || '';
                          if (e && (e.ctrlKey || e.metaKey)) {
                            window.open(url, '_blank');
                          } else {
                            history.push(url);
                          }
                        }}>
                        {title}
                      </WolfTextLink>
                    </>
                  )}
                  {subTitle && (
                    <>
                      <span className="mx-0.5 inline text-sm">/</span>
                      <WolfTextLink
                        variant="body14"
                        component="span"
                        className="inline font-semibold"
                        onClick={(e?: React.MouseEvent) => {
                          const url = subTitleLink || '';
                          if (e && (e.ctrlKey || e.metaKey)) {
                            window.open(url, '_blank');
                          } else {
                            history.push(url);
                          }
                        }}>
                        {subTitle}
                      </WolfTextLink>
                    </>
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            className="flex items-center w-full justify-start h-6"
            onMouseEnter={handleMouseEnterCategory}
            onMouseLeave={handleMouseLeaveCategory}>
            <Typography variant="body14semibold">{localeCommon['category']}</Typography>
            {isHoveringCategory && role !== UserRoles.USER && !isEditingCategory && (
              <Icon onClick={() => setIsEditingCategory(true)} className="ml-1 cursor-pointer p-0 text-sm">
                <EditIcon24 />
              </Icon>
            )}
          </Box>
          {isEditingCategory ? (
            <Box className="flex items-center gap-2 w-full justify-between">
              <WolfInput
                className="w-full"
                label=""
                value={tempCategory}
                autoFocus
                onChange={handleCategoryUpdate}
                onKeyDown={(e) => e.key === 'Enter' && handleCategorySubmit()}
              />
              <Box className="flex items-center gap-2">
                <Icon
                  onClick={() => {
                    setTempCategory(category);
                    setIsEditingCategory(false);
                  }}
                  className="cursor-pointer p-0 text-sm">
                  <CloseOutlined />
                </Icon>
                <Icon onClick={handleCategorySubmit} className="cursor-pointer p-0 text-sm">
                  <CheckBoxOutlined />
                </Icon>
              </Box>
            </Box>
          ) : (
            <Typography
              variant="body14"
              className="!mb-3 w-full"
              onMouseEnter={handleMouseEnterCategory}
              onMouseLeave={handleMouseLeaveCategory}>
              {category || localeCommon['uncategorized']}
            </Typography>
          )}
        </>

        <>
          <Box
            className="flex items-center w-full justify-start h-6"
            onMouseEnter={handleMouseEnterGoal}
            onMouseLeave={handleMouseLeaveGoal}>
            <Typography variant="body14semibold">{localeCommon['goal']}</Typography>
            {isHoveringGoal && role !== UserRoles.USER && !isEditingGoal && (
              <Icon onClick={() => setIsEditingGoal(true)} className="ml-1 cursor-pointer p-0 text-sm">
                <EditIcon24 />
              </Icon>
            )}
          </Box>
          {isEditingGoal ? (
            <Box className="flex items-center justify-between gap-2 w-full">
              <WolfInput className="w-full" label="" fullWidth value={tempGoal} onChange={handleGoalUpdate} autoFocus />
              <Box className="flex items-center gap-2">
                <Icon
                  onClick={() => {
                    setTempGoal(goal);
                    setIsEditingGoal(false);
                  }}
                  className="cursor-pointer p-0 text-sm">
                  <CloseOutlined />
                </Icon>
                <Icon onClick={handleGoalSubmit} className="cursor-pointer p-0 text-sm">
                  <CheckBoxOutlined />
                </Icon>
              </Box>
            </Box>
          ) : (
            <Typography
              variant="body14"
              className="!mb-3 w-full"
              onMouseEnter={handleMouseEnterGoal}
              onMouseLeave={handleMouseLeaveGoal}>
              {goal}
            </Typography>
          )}
        </>
        {generatedFromName && (
          <>
            <Box
              className="flex items-center w-full justify-start h-6"
              onMouseEnter={handleMouseEnterGeneratedFrom}
              onMouseLeave={handleMouseLeaveGeneratedFrom}>
              <Typography variant="body14semibold">{localeProcesses['generatedFrom']}</Typography>
              {isHoveringGeneratedFrom && (
                <WolfTooltip title={localeProcesses['generatedFromClear']} placement="right">
                  <Icon className="ml-1 cursor-pointer p-0 text-sm" onClick={handleClearGeneratedFrom}>
                    <CloseOutlined />
                  </Icon>
                </WolfTooltip>
              )}
            </Box>
            <Typography variant="body14" className="!mb-3 w-full break-words break-all">
              {generatedFromName}
            </Typography>
          </>
        )}
        {
          <Box className="w-full" onMouseEnter={handleMouseEnterPolicies} onMouseLeave={handleMouseLeavePolicies}>
            <Box className="flex items-center w-full justify-start h-6">
              <Typography variant="body14semibold">{localeProcesses['policiesLinked']}</Typography>
              {isHoveringPolicies && role !== UserRoles.USER && !isEditingPolicies && (
                <Icon onClick={handleEditPolicies} className="ml-1 cursor-pointer p-0 text-sm">
                  <EditIcon24 />
                </Icon>
              )}
            </Box>
            {!isEditingPolicies &&
              policies.map((policy) => (
                <Box key={policy.policyId} className="flex items-center w-full justify-start mb-1">
                  <Box className="flex items-center gap-1 min-w-0 flex-1">
                    <Icon className="text-xs flex-shrink-0">
                      <PolicyOutlined />
                    </Icon>
                    <WolfTooltip title={policy.name} placement="right">
                      <Typography variant="body14" className="truncate">{policy.name}</Typography>
                    </WolfTooltip>
                  </Box>
                </Box>
              ))}
            {isEditingPolicies && (
              <Box className="flex items-center gap-2 min-w-40">
                <WolfDropdown
                  showClearButton={true}
                  label={
                    !selectedPolicies
                      ? localeProcesses['selectPolicy']
                      : selectedPolicies.map((item) => item.text).join(', ')
                  }
                  items={policyList}
                  onChange={handlePolicyChange}
                  initialSelectedItems={selectedPolicies.map((item) => item.value)}
                  showBorder={true}
                />
                <Box className="flex items-center gap-2">
                  <Icon onClick={() => setIsEditingPolicies(false)} className="cursor-pointer p-0 text-sm">
                    <CloseOutlined />
                  </Icon>
                  <Icon onClick={handlePolicySubmit} className="cursor-pointer p-0 text-sm">
                    <CheckBoxOutlined />
                  </Icon>
                </Box>
              </Box>
            )}
            {!policies ||
              (policies.length === 0 && !isEditingPolicies && (
                <Typography variant="body14" className="!mb-3 block cursor-pointer" onClick={handleEditPolicies}>
                  {localeProcesses['noPoliciesYet']}
                  <WolfTextLink component="span" size="14px" color="link" className="underline">
                    {' '}
                    {localeCommon['clickToAddOne']}
                  </WolfTextLink>
                </Typography>
              ))}
          </Box>
        }
        <Box onMouseEnter={handleMouseEnterTriggers} onMouseLeave={handleMouseLeaveTriggers} data-tutorial-id="triggers-section">
          <Box className="flex items-center w-full justify-start h-6">
            <Typography variant="body14semibold">{localeCommon['triggers']}</Typography>
            {isHoveringTriggers && role !== UserRoles.USER && (
              <Icon onClick={handleEditTriggers} className="ml-1 cursor-pointer p-0 text-sm">
                <EditIcon24 />
              </Icon>
            )}
          </Box>
          <Box className="flex flex-wrap w-full !mb-3">
            {calendarTriggerList}
            {eventTriggerList}
            {triggeredFrom}
            {triggeredFrom.length === 0 && triggers.length === 0 && (
              <>
                <Typography variant="body14" className="!mt-3 block cursor-pointer" onClick={handleEditTriggers}>
                  {localeProcesses['noTriggersYet'] + ' '}{' '}
                  <WolfTextLink component="span" size="14px" color="link" className="underline">
                    {' '}
                    {localeCommon['clickToAddOne']}
                  </WolfTextLink>
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Box className="flex items-center w-full justify-start h-6">
        <Typography variant="body14semibold">{localeCommon['steps']}</Typography>
      </Box>

      <Box sx={{ position: 'relative' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragEnd={handleUnifiedDragEnd}
          onDragStart={handleUnifiedDragStart}
          onDragOver={handleDragOver}
          measuring={{
            droppable: {
              strategy: 'always' as any,
            },
          }}
          autoScroll={{ enabled: true }}
        >
          <SortableContext items={allStepIds} strategy={verticalListSortingStrategy}>
            <div>
              {/* Phantom drop zone at the start */}
              <PhantomDropZone
                id={PHANTOM_START_ID}
                isVisible={!!activeId}
                isOver={overId === PHANTOM_START_ID}
                text={localeProcesses['dropPlaceAtStart']}
              />
              {stepsList}
              {/* Phantom drop zone at the end */}
              <PhantomDropZone
                id={PHANTOM_END_ID}
                isVisible={!!activeId}
                isOver={overId === PHANTOM_END_ID}
                text={localeProcesses['dropPlaceAtEnd']}
              />
            </div>
          </SortableContext>
          <DragOverlay adjustScale={false} modifiers={[snapCenterToCursor]}>
            {activeStep ? (
              <Box sx={{ opacity: 0.9 }}>
                <WolfCardsStep
                  selected={false}
                  item={{
                    stepId: activeStep.stepId,
                    name: activeStep.name,
                    position: '?',
                    type: null,
                    order: activeStep.order || -1 ,
                  }}
                  index={0}
                  onClick={() => {}}
                  dragDisabled={true}
                  isEditing={false}
                  isDraggingGlobal={false}
                />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Loading overlay during reordering */}
        <Backdrop
          open={isReordering}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1300,
          }}
        >
          <CircularProgress size={40} />
        </Backdrop>
      </Box>

      <Box className="w-full max-w-80">
        <Divider />
        {steps?.length > 0 ? (
          role !== UserRoles.USER && (
            <WolfAddStep
              orientation="horizontal"
              forceExpandAndHover={true}
              hideline={true}
              disabled={disableAddStep}
              disabledMessage={localeProcesses['saveFirstStepBeforeAdding']}
              handleNewStep={(newStep) => handleNewStep(newStep, steps[steps.length - 1]?.order + 1)}
            />
          )
        ) : (
          <Typography variant="body14" className="!mt-3 block">
            {localeProcesses['noStepsYet']}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ProcessSidebar;
